/**
 * Media kit: how a creator presents themselves to brands.
 *
 * Three parts on one page — the profile, the channels (with followers and
 * verification), and best work (brands and showcase posts) — with a live
 * preview of the card brands see in search. The profile and best work save
 * together; channels save one at a time because each is its own record.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import {
  arr,
  cleanRupees,
  compactCount,
  copyText,
  isVerified,
  LANGUAGES,
  lowestPrice,
  languageLabel,
  microsToRupees,
  NICHES,
  nicheLabel,
  PLATFORM_KEYS,
  PLATFORMS,
  platformLabel,
  STATES,
  str,
  type BrandWorked,
  type CreatorChannel,
  type CreatorHomeResponse,
  type CreatorProfile,
  type CreatorType,
  type ShowcaseItem,
  type VerifyResponse,
} from '../lib/creator';
import { formatNumber } from '../lib/format';
import type { ReactNode } from 'react';
import {
  ActionBar,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  SectionTitle,
  Spinner,
  SubHeader,
} from '../components/Primitives';
import { Icon } from '../components/Icon';
import {
  AiHelper,
  Chip,
  CreatorCardPreview,
  ImageUpload,
  PlatformDot,
  Segmented,
  Sheet,
  TagPicker,
  Toggle,
  VerifiedTick,
} from '../components/Creator';

const HANDLE_RE = /^[a-z0-9][a-z0-9_.]{2,29}$/;
type Tab = 'profile' | 'channels' | 'work';

interface Form {
  handle: string;
  displayName: string;
  headline: string;
  bio: string;
  photoUrl: string | null;
  coverUrl: string | null;
  city: string;
  state: string;
  languages: string[];
  niches: string[];
  creatorType: CreatorType;
  audienceNote: string;
  brandsWorked: BrandWorked[];
  showcase: ShowcaseItem[];
  acceptsBarter: boolean;
  minBudget: string;
  isPublic: boolean;
}

const suggestHandle = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^[._]+|[._]+$/g, '')
    .slice(0, 24);

function formFrom(
  p: CreatorProfile | null,
  fallback: { name: string; city: string; state: string },
): Form {
  return {
    handle: p?.handle ?? suggestHandle(fallback.name),
    displayName: p?.displayName ?? fallback.name,
    headline: str(p?.headline),
    bio: str(p?.bio),
    photoUrl: p?.photoUrl ?? null,
    coverUrl: p?.coverUrl ?? null,
    city: p?.city ?? fallback.city,
    state: p?.state ?? fallback.state,
    languages: arr(p?.languages).length ? arr(p?.languages) : ['hindi'],
    niches: arr(p?.niches),
    creatorType: p?.creatorType ?? 'influencer',
    audienceNote: str(p?.audienceNote),
    brandsWorked: arr(p?.brandsWorked),
    showcase: arr(p?.showcase),
    acceptsBarter: Boolean(p?.acceptsBarter),
    minBudget: microsToRupees(p?.minBudgetMicros),
    isPublic: Boolean(p?.isPublic),
  };
}

export function CreatorKitPage() {
  const T = useT();
  const { lang } = useI18n();
  const { vendor, login, refresh } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (
    ['profile', 'channels', 'work'].includes(params.get('tab') ?? '')
      ? params.get('tab')
      : 'profile'
  ) as Tab;
  const setTab = (t: Tab) => setParams(t === 'profile' ? {} : { tab: t }, { replace: true });

  const [data, setData] = useState<CreatorHomeResponse | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [savedHandle, setSavedHandle] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [dirty, setDirtyState] = useState(false);
  // Read inside load(): a reload after a channel change must not wipe
  // profile edits the creator has not saved yet.
  const dirtyRef = useRef(false);
  const setDirty = (v: boolean) => {
    dirtyRef.current = v;
    setDirtyState(v);
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get<CreatorHomeResponse>('/v1/partner/creator');
      setData(r);
      setForm((f) =>
        f && dirtyRef.current
          ? f
          : formFrom(r.profile, {
              name: login?.name || vendor?.contactName || vendor?.businessName || '',
              city: vendor?.baseCity ?? '',
              state: vendor?.baseState ?? '',
            }),
      );
      setSavedHandle(r.profile?.handle ?? null);
      setLoadError(null);
    } catch (err) {
      setLoadError(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    }
  }, [vendor?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
    setDirty(true);
    setMsg(null);
  };

  // ---- live handle check (debounced; your own saved handle is always fine)
  const [handleState, setHandleState] = useState<{
    status: 'idle' | 'checking' | 'ok' | 'taken' | 'invalid';
    suggestion?: string;
  }>({ status: 'idle' });
  const handle = form?.handle ?? '';
  useEffect(() => {
    if (!handle) return setHandleState({ status: 'idle' });
    if (!HANDLE_RE.test(handle)) return setHandleState({ status: 'invalid' });
    if (handle === savedHandle) return setHandleState({ status: 'ok' });
    setHandleState({ status: 'checking' });
    let live = true;
    const id = window.setTimeout(() => {
      api
        .get<{ available: boolean; suggestion?: string }>(
          `/v1/partner/creator/handle-available?handle=${encodeURIComponent(handle)}`,
        )
        .then(
          (r) =>
            live &&
            setHandleState({ status: r.available ? 'ok' : 'taken', suggestion: r.suggestion }),
        )
        // Can't check right now: let the save decide rather than block it.
        .catch(() => live && setHandleState({ status: 'idle' }));
    }, 450);
    return () => {
      live = false;
      window.clearTimeout(id);
    };
  }, [handle, savedHandle]);

  const channels = arr(data?.channels);
  const packages = arr(data?.packages).filter((p) => p.isActive !== false);
  const fromPrice = lowestPrice(packages);

  if (!form && loadError) return <ErrorNote message={loadError} onRetry={() => void load()} />;
  if (!form) return <Spinner />;

  const canSave =
    HANDLE_RE.test(form.handle) &&
    handleState.status !== 'taken' &&
    form.displayName.trim().length >= 2 &&
    !busy;

  async function save() {
    if (!form) return;
    setBusy(true);
    setMsg(null);
    try {
      const budget = cleanRupees(form.minBudget);
      const body = {
        handle: form.handle,
        displayName: form.displayName.trim(),
        headline: form.headline.trim() || undefined,
        bio: form.bio.trim() || undefined,
        photoUrl: form.photoUrl || undefined,
        coverUrl: form.coverUrl || undefined,
        city: form.city.trim() || undefined,
        state: form.state || undefined,
        languages: form.languages,
        niches: form.niches,
        creatorType: form.creatorType,
        audienceNote: form.audienceNote.trim() || undefined,
        brandsWorked: form.brandsWorked
          .filter((b) => b.name.trim())
          .map((b) => ({ name: b.name.trim(), ...(str(b.note) ? { note: str(b.note) } : {}) })),
        showcase: form.showcase
          .filter((s) => s.url.trim())
          .map((s) => ({
            url: s.url.trim(),
            ...(str(s.caption) ? { caption: str(s.caption) } : {}),
            ...(s.platform ? { platform: s.platform } : {}),
            ...(s.imageUrl ? { imageUrl: s.imageUrl } : {}),
          })),
        acceptsBarter: form.acceptsBarter,
        ...(budget ? { minBudget: budget } : {}),
        isPublic: form.isPublic,
      };
      const r = await api.putJson<{ profile: CreatorProfile }>(
        '/v1/partner/creator/profile',
        body,
      );
      setSavedHandle(r.profile?.handle ?? form.handle);
      setDirty(false);
      setMsg({ text: T('सेव हो गया', 'Saved'), ok: true });
      // The first save adds the creator capability: the tabs follow it.
      void refresh();
      void load();
    } catch (err) {
      setMsg({
        text: err instanceof ApiError ? err.message : T('सेव नहीं हुआ।', 'Could not save.'),
        ok: false,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <SubHeader
        back="/creator"
        icon="profile"
        title={T('मीडिया किट', 'Media kit')}
        sub={T('ब्रांड आपको ऐसे देखेंगे', 'How brands will see you')}
      />

      <div className="sticky top-[62px] z-10 -mx-1 mb-4 px-1 pb-1 pt-1">
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          options={[
            ['profile', T('प्रोफ़ाइल', 'Profile')],
            [
              'channels',
              `${T('चैनल', 'Channels')}${channels.length ? ` · ${channels.length}` : ''}`,
            ],
            ['work', T('बेहतरीन काम', 'Best work')],
          ]}
        />
      </div>

      {tab === 'channels' ? (
        <ChannelsSection channels={channels} onChanged={() => void load()} />
      ) : (
        <>
          <div className="space-y-4">
            {tab === 'profile' ? (
              <ProfileFields
                form={form}
                set={set}
                handleState={handleState}
                savedHandle={savedHandle}
                preview={
                  <CreatorCardPreview
                    profile={{ ...form, minBudgetMicros: null }}
                    channels={channels}
                    fromPriceMicros={fromPrice}
                  />
                }
                aiInput={{
                  displayName: form.displayName,
                  headline: form.headline,
                  currentBio: form.bio,
                  niches: form.niches.map((n) => nicheLabel(n, 'en')),
                  languages: form.languages.map((l) => languageLabel(l, 'en')),
                  city: form.city,
                  creatorType: form.creatorType,
                  audienceNote: form.audienceNote,
                  channels: channels.map((c) => ({
                    platform: c.platform,
                    followers: c.followers,
                    engagementPct: c.engagementPct,
                  })),
                  brandsWorked: form.brandsWorked.map((b) => b.name),
                  outputLanguage: lang === 'hi' ? 'hinglish' : 'english',
                }}
              />
            ) : (
              <WorkFields form={form} set={set} />
            )}
          </div>

          <ActionBar>
            {msg && (
              <p className={`mb-2 text-center text-sm ${msg.ok ? 'text-good' : 'text-bad'}`}>
                {msg.text}
              </p>
            )}
            <button
              type="button"
              className="btn-primary"
              disabled={!canSave}
              onClick={() => void save()}
            >
              {busy
                ? T('सेव हो रहा है…', 'Saving…')
                : dirty
                  ? T('मीडिया किट सेव करें', 'Save media kit')
                  : T('सेव करें', 'Save')}
            </button>
          </ActionBar>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ profile

function ProfileFields({
  form,
  set,
  handleState,
  savedHandle,
  preview,
  aiInput,
}: {
  form: Form;
  set: <K extends keyof Form>(k: K, v: Form[K]) => void;
  handleState: {
    status: 'idle' | 'checking' | 'ok' | 'taken' | 'invalid';
    suggestion?: string;
  };
  savedHandle: string | null;
  preview: ReactNode;
  aiInput: Record<string, unknown>;
}) {
  const T = useT();
  const { lang } = useI18n();

  const handleHint =
    handleState.status === 'checking'
      ? T('जाँच रहे हैं…', 'Checking…')
      : handleState.status === 'ok'
        ? form.handle === savedHandle
          ? T('यह आपका है', 'This is yours')
          : T('उपलब्ध है ✓', 'Available ✓')
        : T(
            'छोटे अक्षर, अंक, . और _ — 3 से 30 अक्षर',
            'Lowercase letters, digits, . and _ — 3 to 30 characters',
          );
  const handleError =
    handleState.status === 'taken'
      ? T('यह नाम किसी और का है।', 'Someone already has this.')
      : handleState.status === 'invalid' && form.handle
        ? T(
            '3–30 अक्षर: a-z, 0-9, . _ (शुरुआत अक्षर या अंक से)',
            '3–30 characters: a-z, 0-9, . _ (start with a letter or digit)',
          )
        : null;

  return (
    <>
      <section>
        <p className="eyebrow mb-2 px-1 text-muted">
          {T('ब्रांड को ऐसा दिखेगा', 'Live preview')}
        </p>
        {preview}
      </section>

      <Card className="space-y-4">
        <SectionTitle>{T('फ़ोटो', 'Photos')}</SectionTitle>
        <div className="flex items-start gap-4">
          <ImageUpload
            value={form.photoUrl}
            onChange={(u) => set('photoUrl', u)}
            shape="round"
            label={T('फ़ोटो', 'Photo')}
          />
          <div className="min-w-0 flex-1">
            <ImageUpload
              value={form.coverUrl}
              onChange={(u) => set('coverUrl', u)}
              shape="wide"
              label={T('कवर फ़ोटो', 'Cover photo')}
            />
          </div>
        </div>
        <p className="text-xs text-muted">
          {T(
            'साफ़ चेहरे वाली फ़ोटो और अपने काम की कवर फ़ोटो से ब्रांड ज़्यादा क्लिक करते हैं।',
            'A clear face photo and a cover from your own content get far more clicks from brands.',
          )}
        </p>
      </Card>

      <Card className="space-y-4">
        <Field
          label={T('आपका यूज़रनेम (लिंक में)', 'Your handle (in your link)')}
          hint={handleHint}
          error={handleError}
        >
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted">
              @
            </span>
            <input
              value={form.handle}
              onChange={(e) =>
                set(
                  'handle',
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_.]/g, '')
                    .slice(0, 30),
                )
              }
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="!pl-9"
              placeholder="priya.cooks"
            />
          </div>
        </Field>
        {handleState.status === 'taken' && handleState.suggestion && (
          <button
            type="button"
            className="btn-quiet -mt-2 text-sm"
            onClick={() => set('handle', handleState.suggestion ?? form.handle)}
          >
            {T('इसे लें:', 'Use')} @{handleState.suggestion}
          </button>
        )}

        <Field label={T('दिखने वाला नाम', 'Display name')}>
          <input
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
            maxLength={60}
          />
        </Field>

        <Field
          label={T('एक लाइन में आप', 'Headline')}
          hint={`${form.headline.length}/90 · ${T('जैसे: मेरठ की फ़ूड ब्लॉगर — स्ट्रीट फ़ूड और घर का खाना', 'e.g. Meerut food creator — street food and home cooking')}`}
        >
          <input
            value={form.headline}
            onChange={(e) => set('headline', e.target.value.slice(0, 90))}
          />
        </Field>

        <div>
          <span className="field-label">{T('मैं हूँ', 'I am a')}</span>
          <Segmented<CreatorType>
            value={form.creatorType}
            onChange={(v) => set('creatorType', v)}
            options={[
              ['influencer', T('इन्फ़्लुएंसर', 'Influencer')],
              ['ugc', T('UGC क्रिएटर', 'UGC creator')],
              ['both', T('दोनों', 'Both')],
            ]}
          />
          <p className="mt-1 text-xs text-muted">
            {form.creatorType === 'ugc'
              ? T(
                  'आप ब्रांड के लिए वीडियो/फ़ोटो बनाते हैं; ब्रांड अपने पेज पर चलाता है।',
                  'You make videos/photos for the brand to run on its own pages.',
                )
              : form.creatorType === 'both'
                ? T(
                    'अपने पेज पर पोस्ट भी, और ब्रांड के लिए कंटेंट भी।',
                    'You post on your own pages and also make content for brands.',
                  )
                : T(
                    'आप अपने फ़ॉलोअर के लिए अपने पेज पर पोस्ट करते हैं।',
                    'You post to your own followers on your own pages.',
                  )}
          </p>
        </div>
      </Card>

      <Card className="space-y-3">
        <Field label={T('आपके बारे में', 'Bio')} hint={`${form.bio.length}/600`}>
          <textarea
            rows={5}
            value={form.bio}
            onChange={(e) => set('bio', e.target.value.slice(0, 600))}
            placeholder={T(
              'आप कौन हैं, क्या बनाते हैं, आपकी ऑडियंस कौन है, और ब्रांड को आपके साथ काम करके क्या मिलेगा।',
              'Who you are, what you make, who watches you, and what a brand gets from working with you.',
            )}
          />
        </Field>
        <AiHelper
          task="bio"
          input={aiInput}
          label={T('AI से बायो लिखवाएँ', 'Write my bio with AI')}
          applyLabel={T('बायो में लगाएँ', 'Use as my bio')}
          onApply={(text) => set('bio', text.slice(0, 600))}
          disabled={!form.displayName.trim()}
        />
      </Card>

      <Card className="space-y-4">
        <div>
          <span className="field-label">{T('आपकी कैटेगरी', 'Your niches')}</span>
          <p className="mb-2 text-xs text-muted">
            {T(
              '5 तक चुनें। ब्रांड इन्हीं से ढूँढते हैं।',
              'Pick up to 5. Brands search by these.',
            )}
          </p>
          <TagPicker
            value={form.niches}
            onChange={(v) => set('niches', v)}
            options={NICHES.map(([k]) => k)}
            labelOf={(k) => nicheLabel(k, lang)}
            max={5}
            customPlaceholder={T('अपनी कैटेगरी लिखें', 'Type your own')}
          />
        </div>
        <div>
          <span className="field-label">
            {T('किस भाषा में बनाते हैं', 'Languages you create in')}
          </span>
          <TagPicker
            value={form.languages}
            onChange={(v) => set('languages', v)}
            options={LANGUAGES.map(([k]) => k)}
            labelOf={(k) => languageLabel(k, lang)}
            collapsedCount={8}
            customPlaceholder={T('और भाषा', 'Another language')}
          />
        </div>
      </Card>

      <Card className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={T('शहर', 'City')}>
            <input
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              autoComplete="address-level2"
            />
          </Field>
          <Field label={T('राज्य', 'State')}>
            <select value={form.state} onChange={(e) => set('state', e.target.value)}>
              <option value="">—</option>
              {STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field
          label={T('आपकी ऑडियंस', 'Your audience')}
          hint={T(
            'जैसे: 70% महिलाएँ, 18–34, ज़्यादातर UP और दिल्ली से',
            'e.g. 70% women, 18–34, mostly from UP and Delhi',
          )}
        >
          <textarea
            rows={2}
            value={form.audienceNote}
            onChange={(e) => set('audienceNote', e.target.value.slice(0, 300))}
          />
        </Field>
      </Card>

      <Card className="space-y-4">
        <Toggle
          checked={form.acceptsBarter}
          onChange={(v) => set('acceptsBarter', v)}
          label={T('बार्टर भी चलेगा', 'Open to barter')}
          hint={T(
            'पैसे की जगह प्रोडक्ट लेकर भी काम करेंगे',
            'You will also work for product instead of cash',
          )}
        />
        <Field
          label={`${T('कम से कम बजट (₹)', 'Minimum budget (₹)')} (${T('ज़रूरी नहीं', 'optional')})`}
          hint={T(
            'इससे कम की रिक्वेस्ट नहीं आएँगी',
            'Brands below this cannot send you a request',
          )}
        >
          <input
            inputMode="decimal"
            value={form.minBudget}
            onChange={(e) => set('minBudget', e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="5000"
            className="tnum"
          />
        </Field>
      </Card>

      <Card className="!border-2">
        <Toggle
          checked={form.isPublic}
          onChange={(v) => set('isPublic', v)}
          label={
            form.isPublic
              ? T('प्रोफ़ाइल पब्लिक है', 'Profile is public')
              : T('प्रोफ़ाइल छिपी है', 'Profile is hidden')
          }
          hint={
            form.isPublic
              ? T(
                  'ब्रांड आपको सर्च में देखेंगे और लिंक खुलेगा',
                  'Brands find you in search and your link works',
                )
              : T('तैयार होने पर चालू करें', 'Switch on when you are ready')
          }
        />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- best work

function WorkFields({
  form,
  set,
}: {
  form: Form;
  set: <K extends keyof Form>(k: K, v: Form[K]) => void;
}) {
  const T = useT();
  const { lang } = useI18n();
  const [brand, setBrand] = useState('');

  const addBrand = () => {
    const name = brand.trim();
    if (!name || form.brandsWorked.some((b) => b.name.toLowerCase() === name.toLowerCase()))
      return setBrand('');
    set('brandsWorked', [...form.brandsWorked, { name }]);
    setBrand('');
  };

  const updateShow = (i: number, patch: Partial<ShowcaseItem>) =>
    set(
      'showcase',
      form.showcase.map((s, j) => (j === i ? { ...s, ...patch } : s)),
    );

  return (
    <>
      <Card className="space-y-3">
        <SectionTitle count={form.brandsWorked.length}>
          {T('जिन ब्रांड के साथ काम किया', 'Brands you have worked with')}
        </SectionTitle>
        <p className="-mt-1 text-xs text-muted">
          {T(
            'लोकल दुकानें भी गिनती हैं। जितने ज़्यादा, उतना भरोसा।',
            'Local shops count too. The more, the more trust.',
          )}
        </p>
        {form.brandsWorked.length > 0 && (
          <ul className="space-y-2">
            {form.brandsWorked.map((b, i) => (
              <li
                key={`${b.name}-${i}`}
                className="flex items-start gap-2 rounded-2xl bg-panel-2 p-2"
              >
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-display font-bold"
                  style={{ background: 'var(--sec-wash)', color: 'var(--sec-ink)' }}
                >
                  {b.name.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{b.name}</p>
                  <input
                    value={b.note ?? ''}
                    onChange={(e) =>
                      set(
                        'brandsWorked',
                        form.brandsWorked.map((x, j) =>
                          j === i ? { ...x, note: e.target.value.slice(0, 120) } : x,
                        ),
                      )
                    }
                    placeholder={T('क्या किया (ज़रूरी नहीं)', 'What you did (optional)')}
                    className="!min-h-[40px] text-sm"
                  />
                </div>
                <button
                  type="button"
                  aria-label={T('हटाएँ', 'Remove')}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted hover:bg-panel"
                  onClick={() =>
                    set(
                      'brandsWorked',
                      form.brandsWorked.filter((_, j) => j !== i),
                    )
                  }
                >
                  <Icon name="x" className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addBrand();
              }
            }}
            placeholder={T('ब्रांड का नाम', 'Brand name')}
          />
          <button
            type="button"
            className="btn-soft !w-auto shrink-0 px-4"
            onClick={addBrand}
            disabled={!brand.trim()}
          >
            <Icon name="plus" className="h-4 w-4" />
            {T('जोड़ें', 'Add')}
          </button>
        </div>
      </Card>

      <Card className="space-y-3">
        <SectionTitle count={form.showcase.length}>
          {T('आपका बेहतरीन काम', 'Showcase')}
        </SectionTitle>
        <p className="-mt-1 text-xs text-muted">
          {T(
            'अपनी 3–6 सबसे अच्छी पोस्ट/रील के लिंक। ब्रांड पहले यही देखते हैं।',
            'Links to your 3–6 best posts or reels. This is the first thing brands look at.',
          )}
        </p>
        {form.showcase.map((s, i) => (
          <div key={i} className="space-y-2 rounded-2xl border border-line p-3">
            <div className="flex items-start gap-3">
              <div className="w-20 shrink-0">
                <ImageUpload
                  value={s.imageUrl}
                  onChange={(u) => updateShow(i, { imageUrl: u })}
                  shape="wide"
                  label={T('फ़ोटो', 'Image')}
                />
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <input
                  value={s.url}
                  onChange={(e) =>
                    updateShow(i, {
                      url: e.target.value.trim(),
                      platform: detectPlatform(e.target.value),
                    })
                  }
                  placeholder="https://instagram.com/reel/…"
                  inputMode="url"
                  autoCapitalize="none"
                  className="!min-h-[44px] text-sm"
                />
                <input
                  value={s.caption ?? ''}
                  onChange={(e) => updateShow(i, { caption: e.target.value.slice(0, 140) })}
                  placeholder={T(
                    'एक लाइन: क्या था, कितने व्यू',
                    'One line: what it was, how many views',
                  )}
                  className="!min-h-[44px] text-sm"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              {s.platform ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted">
                  <PlatformDot platform={s.platform} className="h-5 w-5 text-[8px]" />
                  {platformLabel(s.platform, lang)}
                </span>
              ) : (
                <span />
              )}
              <button
                type="button"
                className="btn-quiet text-sm text-bad"
                onClick={() =>
                  set(
                    'showcase',
                    form.showcase.filter((_, j) => j !== i),
                  )
                }
              >
                {T('हटाएँ', 'Remove')}
              </button>
            </div>
          </div>
        ))}
        {form.showcase.length < 9 && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => set('showcase', [...form.showcase, { url: '' }])}
          >
            <Icon name="plus" className="h-5 w-5" />
            {T('पोस्ट जोड़ें', 'Add a post')}
          </button>
        )}
      </Card>
    </>
  );
}

function detectPlatform(url: string): string | null {
  const u = url.toLowerCase();
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  if (u.includes('threads.net')) return 'threads';
  if (u.includes('x.com') || u.includes('twitter.com')) return 'x';
  if (u.includes('linkedin.com')) return 'linkedin';
  if (u.includes('sharechat')) return 'sharechat';
  if (u.includes('moj')) return 'moj';
  if (u.includes('josh')) return 'josh';
  return null;
}

// ----------------------------------------------------------------- channels

interface ChannelForm {
  platform: string;
  username: string;
  url: string;
  followers: string;
  avgViews: string;
  engagementPct: string;
}
const emptyChannel: ChannelForm = {
  platform: 'instagram',
  username: '',
  url: '',
  followers: '',
  avgViews: '',
  engagementPct: '',
};

function ChannelsSection({
  channels,
  onChanged,
}: {
  channels: CreatorChannel[];
  onChanged: () => void;
}) {
  const T = useT();
  const { lang } = useI18n();
  const [editing, setEditing] = useState<{ id: string | null; f: ChannelForm } | null>(null);
  const [verifying, setVerifying] = useState<CreatorChannel | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = channels.reduce((s, c) => s + (Number(c.followers) || 0), 0);

  function open(c?: CreatorChannel) {
    setError(null);
    setEditing(
      c
        ? {
            id: c.id,
            f: {
              platform: c.platform,
              username: c.username,
              url: c.url ?? '',
              followers: String(c.followers ?? ''),
              avgViews: c.avgViews != null ? String(c.avgViews) : '',
              engagementPct: c.engagementPct != null ? String(c.engagementPct) : '',
            },
          }
        : {
            id: null,
            f: {
              ...emptyChannel,
              platform: channels.some((x) => x.platform === 'instagram')
                ? 'youtube'
                : 'instagram',
            },
          },
    );
  }

  async function save() {
    if (!editing) return;
    const f = editing.f;
    const username = f.username.trim().replace(/^@/, '');
    const base = PLATFORMS[f.platform]?.base;
    const body = {
      platform: f.platform,
      username,
      ...(f.url.trim()
        ? { url: f.url.trim() }
        : base && username
          ? { url: `${base}${username}` }
          : {}),
      followers: Number(f.followers) || 0,
      ...(f.avgViews ? { avgViews: Number(f.avgViews) } : {}),
      ...(f.engagementPct ? { engagementPct: Number(f.engagementPct) } : {}),
    };
    setBusy(true);
    setError(null);
    try {
      if (editing.id) await api.putJson(`/v1/partner/creator/channels/${editing.id}`, body);
      else await api.post('/v1/partner/creator/channels', body);
      setEditing(null);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : T('सेव नहीं हुआ।', 'Could not save.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm(T('यह चैनल हटाएँ?', 'Remove this channel?'))) return;
    setBusy(true);
    try {
      await api.del(`/v1/partner/creator/channels/${id}`);
      setEditing(null);
      onChanged();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('हटाया नहीं जा सका।', 'Could not remove.'),
      );
    } finally {
      setBusy(false);
    }
  }

  const f = editing?.f;
  const setF = (patch: Partial<ChannelForm>) =>
    setEditing((e) => (e ? { ...e, f: { ...e.f, ...patch } } : e));
  const canSave = Boolean(
    f && f.username.trim() && Number(f.followers) >= 0 && f.followers !== '',
  );

  return (
    <div className="space-y-4">
      {channels.length > 0 && (
        <div className="card flex items-center justify-between p-4">
          <div>
            <p className="text-xs font-semibold text-muted">
              {T('कुल फ़ॉलोअर', 'Total followers')}
            </p>
            <p className="tnum font-display text-2xl font-bold">{compactCount(total)}</p>
          </div>
          <p className="max-w-[55%] text-right text-xs text-muted">
            {T(
              'वेरिफ़ाइड चैनल पर नीला टिक लगता है और ब्रांड उन्हें पहले देखते हैं।',
              'Verified channels get a blue tick and show up first for brands.',
            )}
          </p>
        </div>
      )}

      {channels.length === 0 ? (
        <EmptyState
          icon="link"
          title={T('अपना पहला चैनल जोड़ें', 'Add your first channel')}
          body={T(
            'Instagram, YouTube या जहाँ भी आप पोस्ट करते हैं — फ़ॉलोअर और औसत व्यू के साथ।',
            'Instagram, YouTube, or wherever you post — with followers and average views.',
          )}
          action={
            <button type="button" className="btn-primary" onClick={() => open()}>
              <Icon name="plus" className="h-5 w-5" />
              {T('चैनल जोड़ें', 'Add channel')}
            </button>
          }
        />
      ) : (
        <ul className="space-y-3">
          {channels.map((c) => (
            <li key={c.id} className="card p-4">
              <div className="flex items-center gap-3">
                <PlatformDot platform={c.platform} className="h-11 w-11 text-sm" />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 font-semibold">
                    <span className="truncate">@{c.username}</span>
                    {isVerified(c) && <VerifiedTick />}
                  </p>
                  <p className="text-xs text-muted">{platformLabel(c.platform, lang)}</p>
                </div>
                <button
                  type="button"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-muted hover:bg-panel-2"
                  aria-label={T('बदलें', 'Edit')}
                  onClick={() => open(c)}
                >
                  <Icon name="edit" className="h-4 w-4" />
                </button>
              </div>
              <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
                {[
                  [T('फ़ॉलोअर', 'Followers'), compactCount(c.followers)],
                  [T('औसत व्यू', 'Avg views'), c.avgViews ? compactCount(c.avgViews) : '—'],
                  [
                    T('एंगेजमेंट', 'Engagement'),
                    c.engagementPct != null ? `${Number(c.engagementPct).toFixed(1)}%` : '—',
                  ],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-panel-2 px-2 py-1.5">
                    <dt className="text-[11px] text-muted">{k}</dt>
                    <dd className="tnum font-display font-bold">{v}</dd>
                  </div>
                ))}
              </dl>
              {isVerified(c) ? (
                <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-good">
                  <Icon name="shield" className="h-4 w-4" />
                  {c.verification === 'api'
                    ? T('API से वेरिफ़ाइड', 'Verified via API')
                    : T('वेरिफ़ाइड', 'Verified')}
                </p>
              ) : (
                <button
                  type="button"
                  className="btn-soft mt-3 !min-h-[44px] text-sm"
                  onClick={() => setVerifying(c)}
                >
                  <Icon name="shield" className="h-4 w-4" />
                  {T('वेरिफ़ाई करें — नीला टिक पाएँ', 'Verify — get the blue tick')}
                </button>
              )}
            </li>
          ))}
          <li>
            <button type="button" className="btn-secondary" onClick={() => open()}>
              <Icon name="plus" className="h-5 w-5" />
              {T('और चैनल जोड़ें', 'Add another channel')}
            </button>
          </li>
        </ul>
      )}

      <Sheet
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        sec="creator"
        title={editing?.id ? T('चैनल बदलें', 'Edit channel') : T('चैनल जोड़ें', 'Add channel')}
        footer={
          <div className="space-y-2 pb-1">
            {error && <p className="text-sm text-bad">{error}</p>}
            <button
              type="button"
              className="btn-primary"
              disabled={!canSave || busy}
              onClick={() => void save()}
            >
              {busy ? T('सेव हो रहा है…', 'Saving…') : T('सेव करें', 'Save')}
            </button>
            {editing?.id && (
              <button
                type="button"
                className="btn-quiet w-full justify-center text-sm text-bad"
                onClick={() => editing.id && void remove(editing.id)}
              >
                <Icon name="trash" className="h-4 w-4" />
                {T('चैनल हटाएँ', 'Remove channel')}
              </button>
            )}
          </div>
        }
      >
        {f && (
          <div className="space-y-4">
            <div>
              <span className="field-label">{T('प्लेटफ़ॉर्म', 'Platform')}</span>
              <div className="flex flex-wrap gap-2">
                {PLATFORM_KEYS.map((p) => (
                  <Chip key={p} on={f.platform === p} onClick={() => setF({ platform: p })}>
                    {platformLabel(p, lang)}
                  </Chip>
                ))}
              </div>
            </div>
            <Field label={T('यूज़रनेम', 'Username')}>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted">
                  @
                </span>
                <input
                  value={f.username}
                  onChange={(e) => setF({ username: e.target.value.replace(/^@/, '').trim() })}
                  autoCapitalize="none"
                  autoCorrect="off"
                  className="!pl-9"
                />
              </div>
            </Field>
            <Field
              label={`${T('प्रोफ़ाइल लिंक', 'Profile link')} (${T('ज़रूरी नहीं', 'optional')})`}
              hint={
                PLATFORMS[f.platform]?.base && f.username
                  ? `${T('खाली छोड़ें तो:', 'Left blank:')} ${PLATFORMS[f.platform]?.base ?? ''}${f.username}`
                  : undefined
              }
            >
              <input
                value={f.url}
                onChange={(e) => setF({ url: e.target.value.trim() })}
                inputMode="url"
                autoCapitalize="none"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={T('फ़ॉलोअर', 'Followers')}
                hint={f.followers ? formatNumber(Number(f.followers) || 0) : undefined}
              >
                <input
                  inputMode="numeric"
                  value={f.followers}
                  onChange={(e) => setF({ followers: e.target.value.replace(/\D/g, '') })}
                  className="tnum"
                />
              </Field>
              <Field
                label={T('औसत व्यू', 'Avg views')}
                hint={f.avgViews ? formatNumber(Number(f.avgViews) || 0) : undefined}
              >
                <input
                  inputMode="numeric"
                  value={f.avgViews}
                  onChange={(e) => setF({ avgViews: e.target.value.replace(/\D/g, '') })}
                  className="tnum"
                />
              </Field>
            </div>
            <Field
              label={T('एंगेजमेंट %', 'Engagement %')}
              hint={T(
                '(औसत लाइक + कमेंट) ÷ फ़ॉलोअर × 100. ईमानदार आँकड़ा ही डालें — ब्रांड जाँचते हैं।',
                '(avg likes + comments) ÷ followers × 100. Be honest — brands check.',
              )}
            >
              <input
                inputMode="decimal"
                value={f.engagementPct}
                onChange={(e) =>
                  setF({ engagementPct: e.target.value.replace(/[^\d.]/g, '').slice(0, 5) })
                }
                className="tnum"
                placeholder="3.5"
              />
            </Field>
          </div>
        )}
      </Sheet>

      <VerifySheet
        channel={verifying}
        onClose={() => setVerifying(null)}
        onVerified={() => {
          onChanged();
        }}
      />
    </div>
  );
}

/**
 * Verify a channel: we issue a short code, the creator puts it in their bio,
 * then taps "check now". Two steps, both on this sheet, so they can switch to
 * Instagram and back without losing their place.
 */
function VerifySheet({
  channel,
  onClose,
  onVerified,
}: {
  channel: CreatorChannel | null;
  onClose: () => void;
  onVerified: () => void;
}) {
  const T = useT();
  const { lang } = useI18n();
  const [state, setState] = useState<VerifyResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const started = useRef<string | null>(null);

  useEffect(() => {
    if (!channel) {
      started.current = null;
      setState(null);
      setError(null);
      return;
    }
    if (started.current === channel.id) return;
    started.current = channel.id;
    setBusy(true);
    api
      .post<VerifyResponse>(`/v1/partner/creator/channels/${channel.id}/verify`, {})
      .then(setState)
      .catch((err) =>
        setError(
          err instanceof ApiError ? err.message : T('कोड नहीं मिला।', 'Could not get a code.'),
        ),
      )
      .finally(() => setBusy(false));
  }, [channel?.id]);

  async function check() {
    if (!channel) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<VerifyResponse>(
        `/v1/partner/creator/channels/${channel.id}/verify`,
        { check: true },
      );
      setState((s) => ({ ...s, ...r, verifyCode: r.verifyCode ?? s?.verifyCode }));
      if (r.verified) onVerified();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('जाँच नहीं हो पाई।', 'Could not check.'),
      );
    } finally {
      setBusy(false);
    }
  }

  const code = state?.verifyCode ?? channel?.verifyCode ?? null;
  const profileUrl =
    channel?.url ||
    (channel && PLATFORMS[channel.platform]?.base
      ? `${PLATFORMS[channel.platform]?.base ?? ''}${channel.username}`
      : null);

  return (
    <Sheet
      open={Boolean(channel)}
      onClose={onClose}
      sec="creator"
      title={T('चैनल वेरिफ़ाई करें', 'Verify channel')}
      footer={
        state?.verified ? (
          <button type="button" className="btn-primary mb-1" onClick={onClose}>
            {T('हो गया', 'Done')}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary mb-1"
            disabled={busy || !code}
            onClick={() => void check()}
          >
            {busy ? T('जाँच रहे हैं…', 'Checking…') : T('अभी जाँचें', 'Check now')}
          </button>
        )
      }
    >
      {channel && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <PlatformDot platform={channel.platform} className="h-10 w-10 text-xs" />
            <div>
              <p className="font-semibold">@{channel.username}</p>
              <p className="text-xs text-muted">{platformLabel(channel.platform, lang)}</p>
            </div>
          </div>

          {state?.verified ? (
            <div className="rounded-2xl bg-emerald-50 p-4 text-center">
              <span className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-sky-500 text-white">
                <Icon name="check" className="h-7 w-7" strokeWidth={3} />
              </span>
              <p className="font-display text-lg font-bold">{T('वेरिफ़ाइड!', 'Verified!')}</p>
              <p className="text-sm text-muted">
                {state.message ||
                  T(
                    'अब आप कोड बायो से हटा सकते हैं।',
                    'You can remove the code from your bio now.',
                  )}
              </p>
              {state.followers != null && (
                <p className="mt-1 text-sm">
                  {T('फ़ॉलोअर अपडेट:', 'Followers updated:')}{' '}
                  <b className="tnum">{formatNumber(state.followers)}</b>
                </p>
              )}
            </div>
          ) : (
            <>
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <StepNum n={1} />
                  <span>{T('यह कोड कॉपी करें', 'Copy this code')}</span>
                </li>
              </ol>
              {busy && !code ? (
                <Spinner label={T('कोड बन रहा है…', 'Getting your code…')} />
              ) : code ? (
                <button
                  type="button"
                  onClick={() => void copyText(code).then(setCopied)}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border-2 border-dashed p-4"
                  style={{ borderColor: 'var(--sec-ink)', background: 'var(--sec-wash)' }}
                >
                  <span
                    className="font-mono text-2xl font-bold tracking-widest"
                    style={{ color: 'var(--sec-ink)' }}
                  >
                    {code}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold">
                    <Icon name={copied ? 'check' : 'copy'} className="h-4 w-4" />
                    {copied ? T('कॉपी हुआ', 'Copied') : T('कॉपी', 'Copy')}
                  </span>
                </button>
              ) : null}
              <ol className="space-y-3 text-sm" start={2}>
                <li className="flex gap-3">
                  <StepNum n={2} />
                  <span>
                    {state?.instructions ||
                      T(
                        'इसे अपने प्रोफ़ाइल बायो में कहीं भी जोड़ें और सेव करें।',
                        'Paste it anywhere in your profile bio and save.',
                      )}
                    {profileUrl && (
                      <a
                        href={profileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-quiet ml-1"
                      >
                        {T('प्रोफ़ाइल खोलें', 'Open profile')}
                      </a>
                    )}
                  </span>
                </li>
                <li className="flex gap-3">
                  <StepNum n={3} />
                  <span>
                    {T('वापस आकर "अभी जाँचें" दबाएँ।', 'Come back and tap "Check now".')}
                  </span>
                </li>
              </ol>
              {state?.message && !state.verified && (
                <p className="rounded-2xl bg-amber-50 p-3 text-sm text-live">{state.message}</p>
              )}
            </>
          )}
          {error && <p className="text-sm text-bad">{error}</p>}
          <p className="text-xs text-muted">
            <Link to="/creator" className="btn-quiet">
              {T('बाद में करें', 'Do this later')}
            </Link>
          </p>
        </div>
      )}
    </Sheet>
  );
}

function StepNum({ n }: { n: number }) {
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold"
      style={{ background: 'var(--sec-wash)', color: 'var(--sec-ink)' }}
    >
      {n}
    </span>
  );
}
